/**
 * Utility functions for managing analysis filters in localStorage
 */

// Analysis filter key names (automatically prefixed with entity-specific prefix)
const ANALYSIS_FILTER_KEYS = [
  'search-term',
  'strip-params', 
  'show-only-traffic',
  'impressions-range',
  'clicks-range',
  'sort-type',
  'distribution-filter',
  'performance-filter',
  'visible-columns',
  'selected-intents',
  'search-terms',
  'search-logic',
  'search-config'
];

/**
 * Generate entity-aware storage key
 * @param {string} baseKey - The base key name
 * @param {string} entityType - 'page', 'keyword', or 'page-keyword'
 * @returns {string} Full storage key with entity prefix
 */
const getEntityStorageKey = (baseKey, entityType) => {
  const prefix = entityType === 'keyword' 
    ? 'queryburst-keyword-analysis-'
    : entityType === 'page-keyword'
    ? 'queryburst-page-keyword-analysis-'
    : 'queryburst-page-analysis-';
  return prefix + baseKey;
};

/**
 * Get entity-specific storage keys object
 * @param {string} entityType - 'page', 'keyword', or 'page-keyword'
 * @returns {Object} Storage keys object for the entity type
 */
export const getAnalysisStorageKeys = (entityType) => ({
  SEARCH_TERM: getEntityStorageKey('search-term', entityType),
  STRIP_PARAMS: getEntityStorageKey('strip-params', entityType),
  SHOW_ONLY_TRAFFIC: getEntityStorageKey('show-only-traffic', entityType),
  IMPRESSIONS_RANGE: getEntityStorageKey('impressions-range', entityType),
  CLICKS_RANGE: getEntityStorageKey('clicks-range', entityType),
  SORT_TYPE: getEntityStorageKey('sort-type', entityType),
  DISTRIBUTION_FILTER: getEntityStorageKey('distribution-filter', entityType),
  PERFORMANCE_FILTER: getEntityStorageKey('performance-filter', entityType),
  PAGE_REPORT_FILTER: getEntityStorageKey('page-report-filter', entityType),
  VISIBLE_COLUMNS: getEntityStorageKey('visible-columns', entityType),
  SELECTED_INTENTS: getEntityStorageKey('selected-intents', entityType),
  SEARCH_TERMS: getEntityStorageKey('search-terms', entityType),
  SEARCH_LOGIC: getEntityStorageKey('search-logic', entityType),
  SEARCH_CONFIG: getEntityStorageKey('search-config', entityType),
});

// Legacy export for backward compatibility (defaults to page analysis)
export const ANALYSIS_STORAGE_KEYS = getAnalysisStorageKeys('page');

// Other related localStorage keys
export const OTHER_STORAGE_KEYS = {
  CURRENT_ANALYSIS_ID: 'queryburst-current-analysis-id',
  CURRENT_PROPERTY: 'queryburst-current-property'
};

/**
 * Clear analysis filters for a specific entity type
 * @param {string} entityType - 'page', 'keyword', or 'page-keyword'
 */
export const clearAnalysisFiltersForEntity = (entityType) => {
  try {
    const prefix = entityType === 'keyword' 
      ? 'queryburst-keyword-analysis-'
      : entityType === 'page-keyword'
      ? 'queryburst-page-keyword-analysis-'
      : 'queryburst-page-analysis-';
    
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    
    console.log('🧹 [Clear Filters] Removing keys for', entityType, ':', keysToRemove);
    
    keysToRemove.forEach(key => {
      const oldValue = localStorage.getItem(key);
      localStorage.removeItem(key);
      
      // Dispatch storage event manually for same-window updates
      window.dispatchEvent(new StorageEvent('storage', {
        key: key,
        oldValue: oldValue,
        newValue: null,
        storageArea: localStorage
      }));
    });
  } catch (error) {
    console.warn('Failed to clear analysis filters for entity:', entityType, error);
  }
};

/**
 * Clear only the analysis filter keys (not the current analysis/property keys)
 * Used when navigating between filter categories within the same analysis
 */
export const clearAnalysisFiltersOnly = () => {
  try {
    // Entity-specific prefixes for analysis filters
    const analysisPrefixes = [
      'queryburst-page-analysis-',
      'queryburst-keyword-analysis-',
      'queryburst-page-keyword-analysis-',
      'queryburst-analysis-' // Legacy prefix for backward compatibility
    ];
    
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && analysisPrefixes.some(prefix => key.startsWith(prefix))) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.warn('Failed to clear analysis filters:', error);
  }
};

/**
 * Helper functions for localStorage operations with analysis filters
 */
export const saveFilterToStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn('Failed to save filter to localStorage:', error);
  }
};

export const loadFilterFromStorage = (key, defaultValue) => {
  try {
    const saved = localStorage.getItem(key);
    return saved !== null ? JSON.parse(saved) : defaultValue;
  } catch (error) {
    console.warn('Failed to load filter from localStorage:', error);
    return defaultValue;
  }
};

/**
 * Clear all analysis filters from localStorage
 * Clears entity-specific analysis keys (page, keyword, page-keyword) and legacy keys
 */
export const clearAnalysisFilters = () => {
  try {
    // Clear analysis filters first
    clearAnalysisFiltersOnly();
    
    // Also clear these specific non-analysis keys
    Object.values(OTHER_STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.warn('Failed to clear analysis filters:', error);
  }
}; 